export const translateBackendError = (errorMessage: string, t: (key: string, options?: any) => string): string => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return errorMessage
  }

  const errorKey = errorMessage.toLowerCase().trim()

  const errorMap: Record<string, string> = {
    'file name, path or mail rule filter are required': 'error_workflow_trigger_filter_required',
    'filename, path or mail rule filter are required': 'error_workflow_trigger_filter_required',
    '"6" is not a valid choice.': 'error_matching_algorithm_invalid',
    matching_algorithm: 'error_matching_algorithm_invalid',
    'this field may not be blank.': 'error_field_blank',
    'this field is required.': 'error_field_required',
    'this field cannot be null.': 'error_field_null',
    'this field may not be null.': 'error_field_null',
    'this field cannot be blank.': 'error_field_blank',
    'group with this name already exists in the selected organization.':
      'error_group_name_exists_in_organization',
    'user must belong to at least one organization.':
      'error_user_must_belong_to_at_least_one_organization',
  }

  const mappedKey = Object.keys(errorMap).find((key) => errorKey.includes(key.toLowerCase()))

  if (mappedKey && errorMap[mappedKey]) {
    const translated = t(errorMap[mappedKey], { defaultValue: errorMessage })
    if (translated !== errorMap[mappedKey]) {
      return translated
    }
  }

  return t(`error_${errorKey.replace(/[^a-z0-9_]/g, '_')}`, { defaultValue: errorMessage })
}

const getFieldLabel = (fieldName: string, t: (key: string, options?: any) => string): string => {
  const fieldLabels: Record<string, string> = {
    assign_title: t('assign_title') || 'assign_title',
    assign_tags: t('assign_tags') || 'assign_tags',
    assign_document_type: t('assign_document_type') || 'assign_document_type',
    assign_correspondent: t('assign_correspondent') || 'assign_correspondent',
    assign_storage_path: t('assign_storage_path') || 'assign_storage_path',
    assign_owner: t('assign_owner') || 'assign_owner',
    assign_custom_fields: t('assign_custom_fields') || 'assign_custom_fields',
    email: t('email') || 'email',
    to: t('to') || 'to',
    subject: t('subject') || 'subject',
    body: t('body') || 'body',
    url: t('url') || 'url',
    type: t('type') || 'type',
  }

  return fieldLabels[fieldName] || fieldName
}

export const formatFieldError = (fieldPath: string, errorMessage: string, t: (key: string, options?: any) => string): string => {
  const translatedError = translateBackendError(errorMessage, t)

  // Handle triggers[1].non_field_errors format
  const triggerNonFieldMatch = fieldPath.match(/triggers\[(\d+)\]\.non_field_errors/)
  if (triggerNonFieldMatch) {
    const triggerIndex = parseInt(triggerNonFieldMatch[1])
    const triggerNumber = triggerIndex + 1
    return t('error_trigger_field', {
      triggerNumber,
      error: translatedError,
      defaultValue: `Okidač ${triggerNumber}: ${translatedError}`,
    })
  }

  // Handle triggers[1].fieldName format
  const triggerFieldMatch = fieldPath.match(/triggers\[(\d+)\]\.(.+)/)
  if (triggerFieldMatch) {
    const triggerIndex = parseInt(triggerFieldMatch[1])
    const triggerNumber = triggerIndex + 1
    const fieldName = triggerFieldMatch[2]
    const fieldLabel = getFieldLabel(fieldName, t)
    return t('error_trigger_field_with_field', {
      triggerNumber,
      field: fieldLabel,
      error: translatedError,
      defaultValue: `Okidač ${triggerNumber}, ${fieldLabel}: ${translatedError}`,
    })
  }

  // Handle triggers[1] format
  const fieldPathMatch = fieldPath.match(/triggers\[(\d+)\]/)
  if (fieldPathMatch) {
    const triggerIndex = parseInt(fieldPathMatch[1])
    const triggerNumber = triggerIndex + 1
    return t('error_trigger_field', {
      triggerNumber,
      error: translatedError,
      defaultValue: `Okidač ${triggerNumber}: ${translatedError}`,
    })
  }

  // Handle actions[1].non_field_errors format
  const actionNonFieldMatch = fieldPath.match(/actions\[(\d+)\]\.non_field_errors/)
  if (actionNonFieldMatch) {
    const actionIndex = parseInt(actionNonFieldMatch[1])
    const actionNumber = actionIndex + 1
    return t('error_action_field', {
      actionNumber,
      error: translatedError,
      defaultValue: `Akcija ${actionNumber}: ${translatedError}`,
    })
  }

  // Handle actions[1].fieldName format (e.g., actions[0].email.to)
  const actionFieldMatch = fieldPath.match(/actions\[(\d+)\]\.(.+)/)
  if (actionFieldMatch) {
    const actionIndex = parseInt(actionFieldMatch[1])
    const actionNumber = actionIndex + 1
    const fieldPathParts = actionFieldMatch[2].split('.')
    const fieldName = fieldPathParts[fieldPathParts.length - 1]
    const fieldLabel = getFieldLabel(fieldName, t)
    return t('error_action_field_with_field', {
      actionNumber,
      field: fieldLabel,
      error: translatedError,
      defaultValue: `Akcija ${actionNumber}, ${fieldLabel}: ${translatedError}`,
    })
  }

  // Handle actions[1] format
  const actionPathMatch = fieldPath.match(/actions\[(\d+)\]/)
  if (actionPathMatch) {
    const actionIndex = parseInt(actionPathMatch[1])
    const actionNumber = actionIndex + 1
    return t('error_action_field', {
      actionNumber,
      error: translatedError,
      defaultValue: `Akcija ${actionNumber}: ${translatedError}`,
    })
  }

  return translatedError
}
